import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

interface SyncDuelContextType {
  socket: Socket | null;
  join: (code: string, name: string, uid?: string) => void;
  toggleReady: (code: string) => void;
  answer: (code: string, index: number) => void;
  leave: (code: string) => void;
  setConfig: (code: string, cfg: { categoryKey?: string | null; categoryKeys?: string[] | null; language?: string; maxQuestions?: number; timePerQuestion?: number }) => void;
  state: {
    code: string;
    players: { id: string; name: string; uid?: string; score: number; ready: boolean; connected?: boolean; stats?: { totalWins: number; winsVs: number; lossesVs: number } }[];
    hostId?: string;
    inGame: boolean;
    paused?: boolean;
    pauseDeadline?: number | null;
    question?: { text: string; options: string[]; endsAt: number; imageUrl?: string; categoryName?: string; categoryEmoji?: string; difficulty?: number } | null;
    tickLeft?: number;
    index?: number;
    total?: number;
    timePerQuestion?: number;
    reveal?: {
      correctIndex: number;
      awarded: Record<string, number>;
      scores: Record<string, number>;
      round?: {
        fastest?: { playerId: string; ms: number } | null;
        answered: Record<string, boolean>;
        correct: Record<string, boolean>;
      };
    } | null;
    categoryKey?: string | null;
    categoryName?: string | null;
    categoryEmoji?: string | null;
    language?: string;
    end?: {
      scores: Record<string, number>;
      winner?: string;
      categoryKey?: string;
      categoryName?: string;
      totals?: Record<string, { correct: number; answered: number; fastestMs?: number }>;
      perPlayer?: Record<string, { 
        correct: number; 
        answered: number; 
        fastestMs?: number;
        byCategory?: Record<string, { 
          correct: number; 
          answered: number; 
          categoryName?: string;
          categoryEmoji?: string;
        }>;
      }>;
      fastest?: { playerId: string; ms: number };
      maxQuestions?: number;
    } | null;
  } | null;
  connected: boolean;
}

const SyncDuelSocketContext = createContext<SyncDuelContextType | undefined>(undefined);
export const useSyncDuelSocket = () => {
  const ctx = useContext(SyncDuelSocketContext);
  if (!ctx) throw new Error('useSyncDuelSocket must be used within SyncDuelSocketProvider');
  return ctx;
};

export const SyncDuelSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [state, setState] = useState<SyncDuelContextType['state']>(null);

  useEffect(() => {
    // @ts-ignore
    const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';
    // Debug: log the URL we connect to
    try { console.log('[SyncDuel] Connecting to', `${API_URL}/syncduel`); } catch {}
    const s = io(`${API_URL}/syncduel`);
    setSocket(s);
    s.on('connect', () => setConnected(true));
    s.on('disconnect', () => setConnected(false));
    s.on('connect_error', (err) => {
      try { console.error('[SyncDuel] connect_error', err?.message || err); } catch {}
      setConnected(false);
    });

    s.on('lobby', (data: any) => setState(prev => {
      // Preserve current question data if lobby sends incomplete question info
      const mergedQuestion = data.question && data.question.categoryName 
        ? data.question 
        : (prev?.question || data.question);
      return { ...(prev || {} as any), ...data, question: mergedQuestion };
    }));
    s.on('start', () => {
      // Clear old localStorage answers when a new game starts
      try {
        const keys = Object.keys(localStorage).filter(k => k.startsWith('syncduel:answered:'));
        keys.forEach(k => localStorage.removeItem(k));
      } catch {}
      setState(prev => prev ? { ...prev, inGame: true, end: null } : prev);
    });
    s.on('question', (q: any) => setState(prev => prev ? { ...prev, question: q, reveal: null, index: q.index, total: q.total, end: null } : prev));
    s.on('tick', (t: any) => setState(prev => prev ? { ...prev, tickLeft: t.left } : prev));
    s.on('paused', (p: any) => setState(prev => prev ? { ...prev, paused: true, pauseDeadline: p?.until ?? null } : prev));
    s.on('resumed', (r: any) => setState(prev => prev ? { ...prev, paused: false, pauseDeadline: null } : prev));
    s.on('reveal', (r: any) => setState(prev => {
      if (!prev) return prev as any;
      const players = (prev.players || []).map(p => ({ ...p, score: r?.scores?.[p.id] ?? p.score }));
      return { ...prev, players, reveal: r };
    }));
    s.on('end', (e: any) => setState(prev => {
      if (!prev) return prev as any;
      const players = (prev.players || []).map(p => ({ ...p, score: e?.scores?.[p.id] ?? p.score }));
      return { ...prev, inGame: false, question: null, reveal: null, end: e || null, players };
    }));

    return () => { s.close(); };
  }, []);

  const join = useCallback((code: string, name: string, uid?: string) => {
    try { console.log('[SyncDuel] join()', { code, name, uid }); } catch {}
    try { localStorage.setItem('syncduel:name', name || ''); } catch {}
    try { if (uid) localStorage.setItem('syncduel:uid', uid); } catch {}
    socket?.emit('join', { code, name, uid });
  }, [socket]);
  const toggleReady = useCallback((code: string) => socket?.emit('toggle-ready', { code }), [socket]);
  const answer = useCallback((code: string, index: number) => socket?.emit('answer', { code, index }), [socket]);
  const leave = useCallback((code: string) => socket?.emit('leave', { code }), [socket]);
  const setConfig = useCallback((code: string, cfg: { categoryKey?: string | null; language?: string; maxQuestions?: number }) => socket?.emit('set-config', { code, ...cfg }), [socket]);

  const value = useMemo(() => ({ socket, join, toggleReady, answer, leave, setConfig, state, connected }), [socket, join, toggleReady, answer, leave, setConfig, state, connected]);
  return <SyncDuelSocketContext.Provider value={value}>{children}</SyncDuelSocketContext.Provider>;
};
